import style from './style.module.css';
import formStyles from '../style.module.css';
import { Color } from '@tiptap/extension-color';
import Link from '@tiptap/extension-link';
import ListItem from '@tiptap/extension-list-item';
import TextStyle from '@tiptap/extension-text-style';
import {
  Editor as EditorType,
  EditorProviderProps,
  EditorContent,
  useEditor,
} from '@tiptap/react';
import { Markdown } from 'tiptap-markdown';
import StarterKit from '@tiptap/starter-kit';
import { FormItem } from 'fave-ui';
import { getIn, useFormikContext } from 'formik';
import { useCallback } from 'react';
import { cn } from '../../../../helpers/cn';
import useOnceCallback from '../../../../hooks/useOnceCallback';

type Props<T> = {
  name: keyof T & (string | undefined);
  label: string;
  disabled?: boolean;
} & EditorProviderProps;

const Editor = <T extends {}>({ name, label, disabled }: Props<T>) => {
  const { errors, touched, values, setFieldValue, setFieldTouched } =
    useFormikContext<T>();
  const isArrayField = name.includes('.');

  const innerValue = isArrayField
    ? getIn(values, name)
    : (values[name] as string);
  const error = isArrayField ? getIn(errors, name) : errors[name];
  const isTouched = isArrayField ? getIn(touched, name) : touched[name];
  const helpText = isTouched && error !== undefined && error;
  const showError = error !== undefined && isTouched;

  const extensions = [
    Color.configure({ types: [TextStyle.name, ListItem.name] }),
    StarterKit.configure({
      bulletList: {
        keepMarks: true,
      },
      orderedList: {
        keepMarks: true,
      },
    }),
    Link.configure({
      openOnClick: false,
      autolink: true,
      defaultProtocol: 'https',
    }),
    Markdown,
  ];

  const handleChange = (name: string, e: EditorType) => {
    // to set the value of the editor to the formik field
    // if editor without formatting is empty, then set the value to empty string
    // else return the markdown value of the editor
    // on admin tool they are using markdown for the editor
    const newValue = !!e.getText() ? e.storage.markdown.getMarkdown() : '';
    setFieldValue(name, newValue);
  };

  const editor = useEditor({
    extensions,
    editorProps: {
      attributes: {
        class: cn(style.editorPropsContainer, disabled && style.disabled),
      },
    },
    onUpdate: ({ editor }) => {
      if (!disabled) handleChange(name, editor);
    },
    onBlur: () => setFieldTouched(name, true, true),
    onCreate: ({ editor }) =>
      editor.setOptions({
        content: innerValue,
        editable: !disabled,
        editorProps: { nodeViews: {} },
      }),
  });

  // programmatically set the value of the editor
  useOnceCallback(
    () => editor?.commands.setContent(innerValue),
    // runs when innerValue and editor are not null
    !!(innerValue && editor),
  );

  return (
    <FormItem
      className={cn(formStyles.formItem, showError && style.error)}
      label={label}
      validateStatus={showError ? 'error' : 'success'}
      help={helpText}
    >
      <MenuBar editor={editor} disabled={disabled} />
      <EditorContent
        editor={editor}
        className={cn(style.editorContainer, disabled && style.disabled)}
      />
    </FormItem>
  );
};

// contains all text styling options
const MenuBar = ({
  editor,
  disabled,
}: {
  editor: EditorType | null;
  disabled?: boolean;
}) => {
  if (!editor) {
    return null;
  }

  // to set link to selected text in editor
  const setLink = useCallback(() => {
    const url = window.prompt('Enter your URL');

    if (url === null) return;

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    const hasProtocol = /^https?:\/\//.test(url);
    const formattedUrl = hasProtocol ? url : `https://${url}`;

    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: formattedUrl })
      .run();
  }, [editor]);

  // more text styling options can be found here
  // https://tiptap.dev/docs/editor/extensions/nodes
  return (
    <div className={cn(style.controlGroup, disabled && style.disabled)}>
      <div className={cn(style.buttonGroup, disabled && style.disabled)}>
        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={disabled}
          className={editor.isActive('bold') ? style.isActive : ''}
        >
          Bold
        </button>
        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={disabled}
          className={editor.isActive('italic') ? style.isActive : ''}
        >
          Italic
        </button>
        <button
          onClick={() => editor.chain().focus().setParagraph().run()}
          disabled={disabled}
          className={editor.isActive('paragraph') ? style.isActive : ''}
        >
          Paragraph
        </button>
        <button
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 1 }).run()
          }
          disabled={disabled}
          className={
            editor.isActive('heading', { level: 1 }) ? style.isActive : ''
          }
        >
          H1
        </button>
        <button
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
          disabled={disabled}
          className={
            editor.isActive('heading', { level: 2 }) ? style.isActive : ''
          }
        >
          H2
        </button>
        <button
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 3 }).run()
          }
          disabled={disabled}
          className={
            editor.isActive('heading', { level: 3 }) ? style.isActive : ''
          }
        >
          H3
        </button>
        <button
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 4 }).run()
          }
          disabled={disabled}
          className={
            editor.isActive('heading', { level: 4 }) ? style.isActive : ''
          }
        >
          H4
        </button>
        <button
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 5 }).run()
          }
          disabled={disabled}
          className={
            editor.isActive('heading', { level: 5 }) ? style.isActive : ''
          }
        >
          H5
        </button>
        <button
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 6 }).run()
          }
          disabled={disabled}
          className={
            editor.isActive('heading', { level: 6 }) ? style.isActive : ''
          }
        >
          H6
        </button>
        <button
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          disabled={disabled}
          className={editor.isActive('blockquote') ? style.isActive : ''}
        >
          Blockquote
        </button>
        <button
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          disabled={disabled}
          className={editor.isActive('bulletList') ? style.isActive : ''}
        >
          Bullet list
        </button>
        <button
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          disabled={disabled}
          className={editor.isActive('orderedList') ? style.isActive : ''}
        >
          Ordered list
        </button>
        <button
          onClick={setLink}
          disabled={disabled}
          className={editor.isActive('link') ? style.isActive : ''}
        >
          Set link
        </button>
        <button
          onClick={() => editor.chain().focus().unsetLink().run()}
          disabled={disabled}
        >
          Unset link
        </button>
      </div>
    </div>
  );
};

export default Editor;
